import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { showNotification } from '@helpers/show-toast';
import { AuthService } from '@services/auth/auth.service';
import { PermissionService } from '@services/permission.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { RocheDoctorPermission } from 'src/app/_constants/permissions';
import { baseUrl } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing: boolean = false;
  private pendingRequests: number = 0;

  constructor(
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private router: Router,
    public toastr: ToastrService,
    private permissionService: PermissionService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authToken = this.authService.getToken();

    let reqClone: any;

    let pattern = /^https?:\/\//g;

    if (authToken) {
      reqClone = this.addAccessTokenToHeader(request);
    } else {
      if (pattern.test(request?.url)) {
        reqClone = request.clone({ url: `${request.url}` });
      } else {
        reqClone = request.clone({ url: `${baseUrl}${request.url}` });
      }
    }

    // Increment pending requests counter and show spinner if it's the first request
    this.pendingRequests++;
    if (this.pendingRequests === 1) {
      this.spinner.show();
    }

    return next.handle(reqClone).pipe(
      catchError((error) => {
        if (!navigator.onLine) {
          showNotification(
            'warning',
            `There is no internet.. Check the internet connection.`,
            this.toastr
          );
        } else if (error.status === 429) {
          showNotification(
            'warning',
            `There are too many incorrect requests.. Please try again later.`,
            this.toastr
          );
        }
        else if (error instanceof HttpErrorResponse
          && !reqClone.url.toLowerCase().includes('login')
          && error.status === 401) {
          return this.handle401Error(request, next);
        } else if (error.status === 400) {
          const errorMsg = this.getErrorMessage(error);

          // Skip notification for suspended account in login component. Not a graceful way to handle it, but I'm short on time. Sorry :)
          if (errorMsg?.toLowerCase().includes('suspended')) {
            return throwError(error);
          }

          // Show notification for all other 400 errors
          showNotification('danger', errorMsg, this.toastr);
        }
        else if (error.status === 403) {
          // showNotification(
          //   'warning',
          //   `Access Denied.. You do not have permission to access this resource.`,
          //   this.toastr
          // );
        } else if (error.status == 0) {
          let MSG = '';
          if (error.error.errorList) {
            MSG = error?.error?.errorList[0]?.message
          } else if (error?.errors) {
            MSG = error?.errors?.tittle
          } else {
            MSG = 'unknown error'
          }
          showNotification(
            'danger',
            MSG,
            this.toastr
          );
        }
        else {
          showNotification(
            'danger',
            `Server Error: some thing went wrong!`,
            this.toastr
          );
        }
        return throwError(error.message || 'Server Error!');
      }),
      finalize(() => {
        // Decrement pending requests counter and hide spinner if no more requests are pending
        this.pendingRequests--;
        if (this.pendingRequests === 0) {
          this.spinner.hide();
        }
      })
    );
  }

  private getErrorMessage(error: any) {
    return error.error
      ? error?.error?.errorList[0]?.message
      : error?.errors?.title;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      let tokenData = this.authService.getAccessAndRefreshToken();
      return this.authService.refreshToken(tokenData).pipe(
        switchMap((tokenDataResponse) => {
          this.isRefreshing = false;
          this.authService.setLoginResponseInLocalStorage(tokenDataResponse.data);
          const updatedRequest = this.addAccessTokenToHeader(request);
          if (updatedRequest) {
            return next.handle(updatedRequest);
          } else {
            return throwError(() => new Error('Failed to add access token to header.'));
          }
        }),
        catchError((error) => {
          this.isRefreshing = false;

          const loginRoute = this.permissionService.hasPermission(RocheDoctorPermission) ? 'auth/login/phone' : 'auth/login';

          if (this.authService.getAccessAndRefreshToken().accessToken)
            this.authService.logoutLocalStorageClear();

          this.router.navigateByUrl(loginRoute);

          return throwError(() => error);
        })
      );
    }

    const updatedRequest = this.addAccessTokenToHeader(request);
    if (updatedRequest) {
      return next.handle(updatedRequest);
    } else {
      return throwError(() => new Error('Failed to add access token to header.'));
    }
  }

  private addAccessTokenToHeader(request: HttpRequest<any>) {
    let tokens = this.authService.getAccessAndRefreshToken();
    let reqClone;
    let pattern = /^https?:\/\//g;

    if (tokens.accessToken) {
      const headers = new HttpHeaders({
        Authorization: 'Bearer ' + tokens.accessToken,
      });
      if (pattern.test(request?.url)) {
        reqClone = request.clone({ url: `${request.url}` });
      } else {
        reqClone = request.clone({ headers, url: `${baseUrl}${request.url}` });
      }
    }
    return reqClone;
  }
}